import axios from "axios"
import { DefaultFunctionURL } from "./url.service"

//==================================================================================
// Locale header for every request to send the selected language to the API 
//==================================================================================
axios.interceptors.request.use((config) => {
  const lng =
    localStorage.getItem('lng') ||  // User's language preference
    navigator.language ||  // fallback to browser language (e.g., "pt-PT")
    'pt';    // last resort fallback is pt (portuguese) by default

  // Set the locale header for the request
  config.headers = config.headers || {};
  config.headers['Accept-Language'] = lng;

  return config;
});

//==================================================================================
// Helper to attach success metadata to API responses
//==================================================================================
function attachOkMeta(data, status) {
    // Wrap primitives (string, number, boolean, null, undefined) so we can attach metadata
    const isObject = data !== null && typeof data === 'object'
    const payload = isObject ? data : { data }  // <- wrap primitives

    try {
        Object.defineProperty(payload, '__ok', { value: true, enumerable: false })
        Object.defineProperty(payload, '__status', { value: status, enumerable: false })
    } catch { /* ignore */ }

    return payload
}

//==================================================================================
// Helper to attach error metadata to API errors
// This function adds metadata to the error object to help identify it as an API error
// and provides a user-friendly message.
//==================================================================================
function attachApiErrorMeta(err) {
    const api = (err && err.response && err.response.data !== undefined)
        ? err.response.data
        : err

    // Only attach metadata if the error is an object
    if (api && typeof api === 'object') {
        const msg =
            api.message ||
            api.Message ||
            api.title ||
            api.detail ||
            (Array.isArray(api.errors) && api.errors.filter(Boolean).join(', ')) ||
            (Array.isArray(api.Errors) && api.Errors.filter(Boolean).join(', ')) ||
            (typeof api.error === 'string' ? api.error : null) ||
            'Erro'

        try {
            // Non-enumerable flags to prevent them from appearing in simple loops/serializations
            Object.defineProperty(api, '__isApiError', { value: true, enumerable: false })
            Object.defineProperty(api, '__message', { value: msg, enumerable: false })
            Object.defineProperty(api, '__ok', { value: false, enumerable: false })
            if (err?.response?.status) {
                Object.defineProperty(api, '__status', { value: err.response.status, enumerable: false })
            }
        } catch { /* ignore */ }
    }

    return api
}

//======================================================================================
//              MAIN CLASS
//======================================================================================
export class BufferService {

    //==================================================================================
    // Constructor
    //==================================================================================
    constructor(name, baseUrl) {
        // If baseUrl is passed, use it. Otherwise fallback
        this.url = baseUrl ?? DefaultFunctionURL()
        this.entityName = name
    }

    //==================================================================================
    // Method to get 
    //==================================================================================
    async get() {
        const lists = await axios.get(this.url + this.entityName)
            .then(function (response) {
                if (response.name === 'AxiosError') {
                    return response
                }
                return attachOkMeta(response.data, response.status)
            })
            .catch(function (error) {
                return attachApiErrorMeta(error)
            })
        return lists
    }

    //==================================================================================
    // Method to get by ID
    //==================================================================================
    async getId(id) {
        const lists = await axios.get(this.url + this.entityName + '/' + id)
            .then(function (response) {
                if (response.name === 'AxiosError') {
                    return response
                }
                return attachOkMeta(response.data, response.status)
            })
            .catch(function (error) {
                return attachApiErrorMeta(error)
            })
        return lists
    }

    //==================================================================================
    //Method to fetch the table metadata
    //==================================================================================
    async options() {
        const metadata = await axios.options(this.url + 'OPTIONS?context=' + this.entityName + '&lang=' + `${localStorage.getItem('lng') ? localStorage.getItem('lng') : 'pt'}`)
            .then(function (response) {
                if (response.name === 'AxiosError') {
                    return response
                }
                return attachOkMeta(response.data, response.status)
            })
            .catch(function (error) {
                return attachApiErrorMeta(error)
            })
        return metadata
    }

    //==================================================================================
    // Method to post data
    //==================================================================================
    async post(data) {
        const res = await axios.post(this.url + this.entityName, data)
            .then(function (response) {
                if (response.name === 'AxiosError') {
                    return response
                }
                return attachOkMeta(response.data, response.status)
            })
            .catch(function (error) {
                return attachApiErrorMeta(error)
            })
        return res
    }

    //==================================================================================
    // Method to post data with ID
    //==================================================================================
    async postId(id) {
        const lists = await axios.post(this.url + this.entityName + '/' + id)
            .then(function (response) {
                if (response.name === 'AxiosError') {
                    return response
                }
                return attachOkMeta(response.data, response.status)
            })
            .catch(function (error) {
                return attachApiErrorMeta(error)
            })
        return lists
    }

    //==================================================================================
    // Method to post query data
    //==================================================================================
    async postQuery(data) {
        const res = await axios.post(this.url + this.entityName + data)
            .then(function (response) {
                if (response.name === 'AxiosError') {
                    return response
                }
                return attachOkMeta(response.data, response.status)
            })
            .catch(function (error) {
                return attachApiErrorMeta(error)
            })
        return res
    }

    //==================================================================================
    // Method to delete data by ID
    //==================================================================================
    async delete(id) {
        const res = await axios.delete(this.url + this.entityName + '/' + id)
            .then(function (response) {
                if (response.name === 'AxiosError') {
                    return response
                }
                return attachOkMeta(response.data, response.status)
            })
            .catch(function (error) {
                return attachApiErrorMeta(error)
            })
        return res
    }

    //==================================================================================
    // Method to put query data
    //==================================================================================
    async putQuery(data) {
        const res = await axios.put(this.url + this.entityName + data)
            .then(function (response) {
                if (response.name === 'AxiosError') {
                    return response
                }
                return attachOkMeta(response.data, response.status)
            })
            .catch(function (error) {
                return attachApiErrorMeta(error)
            })
        return res
    }

    //==================================================================================
    // Method to put data
    //==================================================================================
    async put(data) {
        const res = await axios.put(this.url + this.entityName, data)
            .then(function (response) {
                if (response.name === 'AxiosError') {
                    return response
                }
                return attachOkMeta(response.data, response.status)
            })
            .catch(function (error) {
                return attachApiErrorMeta(error)
            })
        return res
    }

    //==================================================================================
    // Method to update data by ID
    //==================================================================================
    async update(id, data) {
        const res = await axios.put(this.url + this.entityName + '/' + id, data)
            .then(function (response) {
                if (response.name === 'AxiosError') {
                    return response
                }
                return attachOkMeta(response.data, response.status)
            })
            .catch(function (error) {
                return attachApiErrorMeta(error)
            })
        return res
    }

    //==================================================================================
    // Method to filter data
    //==================================================================================
    async filter(filter) {
        const filterList = await axios.get(this.url + this.entityName + filter)
            .then(function (response) {
                if (response.name === 'AxiosError') {
                    return response
                }
                return attachOkMeta(response.data, response.status)
            })
            .catch(function (error) {
                return attachApiErrorMeta(error)
            })
        return filterList
    }
}
